import { useCallback, useEffect, useRef, useState } from 'react'
import { FileText, Paperclip, Trash2, Upload } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { FeldStatusZeichen, useFeld, type Speicherstelle } from '@/lib/speichern'
import { deleteCalculationFile, fetchCalculationFiles, oeffneKalkulationsdatei, uploadCalculationFile, type DateiZiel } from '../api/calculations.api'
import { WAEHLBARE_DATEIARTEN, DATEIART_ANZEIGE } from '../lib/kalkulation-labels'
import { useZeilenStelle } from '../lib/kalkulation-einstellungen'
import type { CalcFile } from '../types/calculation.types'

/** Detailblatt O 4a.2 — höchstens so viele Dateien je Position und je Materialzeile (wie im Backend). */
export const MAX_DATEIEN_JE_STELLE = 10

const ARTEN = WAEHLBARE_DATEIARTEN

export function dateiGroesse(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** „1 Datei" / „3 Dateien". */
export const dateienText = (n: number) => `${n} ${n === 1 ? 'Datei' : 'Dateien'}`

/** 4a.2: an der Büroklammer zusammen, im Hinweis getrennt nach Position und Materialzeilen. */
export const dateienHinweis = (gesamt: number, anZeilen: number) =>
  `${dateienText(gesamt)} — ${gesamt - anZeilen} an der Position, ${anZeilen} an Materialzeilen`

/** Die Büroklammer mit Zahl für Liste, Übersicht und Kopf — nichts, solange keine Datei hängt. */
export function DateiMarke({ gesamt, anZeilen, className = '' }: { gesamt: number; anZeilen: number; className?: string }) {
  if (!gesamt) return null
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] tabular-nums text-muted-foreground ${className}`} title={dateienHinweis(gesamt, anZeilen)}>
      <Paperclip className="h-3 w-3" />
      {gesamt}
    </span>
  )
}

/**
 * Mehrere Dateien nacheinander an eine Stelle hängen — einzeln, damit eine
 * abgelehnte Datei (Typ, Größe, Obergrenze) die übrigen nicht mitnimmt.
 * Liefert, was geklappt hat, und die Meldungen der übrigen.
 */
export async function haengeAn(kalkulationId: string, dateien: File[], ziel: DateiZiel): Promise<{ ok: number; fehler: string[] }> {
  let ok = 0
  const fehler: string[] = []
  for (const d of dateien) {
    try {
      await uploadCalculationFile(kalkulationId, d, ziel)
      ok++
    } catch (e) {
      fehler.push(`${d.name}: ${apiErrorText(e, 'nicht abgelegt')}`)
    }
  }
  return { ok, fehler }
}

/**
 * AP-37 Nachtrag 3 (Detailblatt O 4a) — **das Dateifeld** einer Position bzw.
 * einer Materialzeile: Liste, Hochladen, Öffnen, Entfernen, Dateiart. Muster
 * `ProjectFilesPanel` der Stückliste.
 *
 * An der Position zeigt es auch die Dateien ihrer Materialzeilen (Spalte
 * „gehört zu"); hochgeladen wird an die Position selbst. Die Dateien stehen
 * zugleich im Reiter Dokumente — dieselbe Liste, nur gefiltert.
 */
export function KalkulationsDateien({
  kalkulationId,
  ziel,
  darf,
  onGeaendert,
  kompakt = false,
}: {
  kalkulationId: string
  /** `costLineId` → nur die Zeile; sonst die Position samt ihrer Zeilen. */
  ziel: { positionId: string; costLineId?: string | null }
  darf: boolean
  onGeaendert?: () => void
  /** Unter einer Blattzeile: ohne eigene Überschrift. */
  kompakt?: boolean
}) {
  const [dateien, setDateien] = useState<CalcFile[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const eingabe = useRef<HTMLInputElement>(null)
  const anZeile = Boolean(ziel.costLineId)

  const laden = useCallback(() => {
    fetchCalculationFiles(kalkulationId, anZeile ? { costLineId: ziel.costLineId } : { positionId: ziel.positionId })
      .then(setDateien)
      .catch((e) => setFehler(apiErrorText(e, 'Die Dateien ließen sich nicht laden.')))
  }, [kalkulationId, anZeile, ziel.costLineId, ziel.positionId])
  useEffect(laden, [laden])

  // Die Obergrenze zählt, was **direkt** an dieser Stelle hängt — an der Position ohne die Zeilen.
  const hier = (dateien ?? []).filter((d) => (anZeile ? d.costLineId === ziel.costLineId : !d.costLineId))
  const voll = hier.length >= MAX_DATEIEN_JE_STELLE

  const ablegen = async (liste: FileList | File[] | null) => {
    if (!liste?.length) return
    setFehler(null)
    setLaedt(true)
    try {
      const r = await haengeAn(kalkulationId, Array.from(liste), anZeile ? { costLineId: ziel.costLineId } : { positionId: ziel.positionId })
      if (r.fehler.length) setFehler(r.fehler.join(' · '))
      if (r.ok) {
        laden()
        onGeaendert?.()
      }
    } finally {
      setLaedt(false)
      if (eingabe.current) eingabe.current.value = ''
    }
  }

  return (
    <div
      className={kompakt ? 'rounded-md border border-dashed border-border bg-muted/20 px-3 py-2' : 'space-y-2'}
      onDragOver={(e) => darf && e.dataTransfer.types.includes('Files') && e.preventDefault()}
      onDrop={(e) => {
        if (!darf || !e.dataTransfer.files.length) return
        e.preventDefault()
        e.stopPropagation()
        void ablegen(e.dataTransfer.files)
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5" />
          {anZeile
            ? 'Dateien dieser Materialzeile — sie gehen mit einer Materialanfrage an den Lieferanten mit.'
            : 'Dateien der Position und ihrer Materialzeilen. Direkt an der Position gehen sie nicht mit einer Materialanfrage hinaus, nur die der Materialzeilen.'}
        </span>
        {darf && (
          <>
            <input ref={eingabe} type="file" multiple className="hidden" onChange={(e) => void ablegen(e.target.files)} />
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
              disabled={laedt || voll}
              title={voll ? `Höchstens ${MAX_DATEIEN_JE_STELLE} Dateien je ${anZeile ? 'Materialzeile' : 'Position'}` : 'Oder Dateien hierher ziehen'}
              onClick={() => eingabe.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" /> {laedt ? 'Lädt hoch …' : 'Hochladen'}
            </button>
          </>
        )}
      </div>
      {fehler && <p className="text-xs text-destructive">{fehler}</p>}
      {dateien === null ? (
        <p className="py-2 text-xs text-muted-foreground">Lädt …</p>
      ) : dateien.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">{darf ? 'Noch keine Dateien — hierher ziehen oder „Hochladen".' : 'Keine Dateien.'}</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {dateien.map((d) => (
              <DateiZeile
                key={d.id}
                kalkulationId={kalkulationId}
                d={d}
                darf={darf}
                gehoertZu={anZeile ? null : d.costLine ? d.costLine.name : 'Position'}
                onFehler={setFehler}
                onLoeschen={async () => {
                  if (!(await bestaetige(`„${d.file.originalName}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true }))) return
                  try {
                    await deleteCalculationFile(kalkulationId, d.id)
                    laden()
                    onGeaendert?.()
                  } catch (e) {
                    setFehler(apiErrorText(e, 'Die Datei ließ sich nicht entfernen.'))
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function DateiZeile({
  kalkulationId,
  d,
  darf,
  gehoertZu,
  onFehler,
  onLoeschen,
}: {
  kalkulationId: string
  d: CalcFile
  darf: boolean
  gehoertZu: string | null
  onFehler: (t: string) => void
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcFile', `/calculations/${kalkulationId}/files/${d.id}`, d, d.file.originalName) as Speicherstelle<unknown>
  const art = useFeld<string>(stelle, 'kind', { art: 'auswahl', label: 'Dateiart', gesperrt: !darf })
  return (
    <tr className="border-t border-border/60 align-middle">
      <td className="py-1 pr-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-left text-primary hover:underline"
          onClick={() => oeffneKalkulationsdatei(kalkulationId, d.id, d.file.originalName).catch((e) => onFehler(apiErrorText(e, 'Die Datei lässt sich nicht öffnen.')))}
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="break-all">{d.file.originalName}</span>
        </button>
      </td>
      {gehoertZu !== null && <td className="whitespace-nowrap py-1 pr-2 text-xs text-muted-foreground">{gehoertZu}</td>}
      <td className="py-1 pr-2">
        <span className="flex items-center gap-1">
          <select className="rounded border border-border bg-background px-1 py-0.5 text-xs disabled:border-transparent disabled:bg-transparent" aria-label="Dateiart" {...art.props}>
            {ARTEN.map((a) => (
              <option key={a} value={a}>
                {DATEIART_ANZEIGE[a]}
              </option>
            ))}
          </select>
          <FeldStatusZeichen status={art} />
        </span>
      </td>
      <td className="whitespace-nowrap py-1 pr-2 text-right text-xs text-muted-foreground">{dateiGroesse(d.file.sizeBytes)}</td>
      <td className="w-6 py-1 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="entfernen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}
